import React, { useEffect, useRef, useState } from 'react';

interface Commune {
  nom: string;
  departement?: { nom: string };
}

/** Autocomplete ville — API Géo gouv.fr (réutilisable alertes / profil). */
const LocationAutocomplete: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string }> = ({
  value,
  onChange,
  placeholder = "Ex: Paris, Lyon, Île-de-France...",
}) => {
  const [suggestions, setSuggestions] = useState<Commune[]>([]);
  const [open, setOpen] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (val: string) => {
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(val)}&fields=nom,departement&boost=population&limit=8`,
        );
        const data: Commune[] = await res.json();
        setSuggestions(data);
        setOpen(true);
        setHoveredIdx(-1);
      } catch {
        setSuggestions([]);
      }
    }, 250);
  };

  const select = (commune: Commune) => {
    onChange(`${commune.nom}${commune.departement ? ` (${commune.departement.nom})` : ''}`);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={value}
        onChange={e => handleChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none bg-white text-gray-800"
        style={{ borderColor: '#c7d7f5' }}
        onFocus={e => {
          e.target.style.borderColor = '#2a52a8';
          if (suggestions.length > 0) setOpen(true);
        }}
        onBlur={e => {
          e.target.style.borderColor = '#c7d7f5';
        }}
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-20 w-full mt-1 bg-white rounded-xl border shadow-lg overflow-hidden"
          style={{ borderColor: '#c7d7f5' }}
        >
          {suggestions.map((commune, i) => (
            <button
              key={`${commune.nom}-${i}`}
              type="button"
              onMouseDown={() => select(commune)}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(-1)}
              className="w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between"
              style={
                hoveredIdx === i ? { background: '#2a52a8', color: 'white' } : { background: 'white', color: '#374151' }
              }
            >
              <span>{commune.nom}</span>
              {commune.departement && <span className="text-xs opacity-60 ml-2">{commune.departement.nom}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
